import { useState, useEffect, useMemo } from 'react';
import { D, st, toArr, apiGet, API_URL, getToken, apiErrorMessage } from '../constants';

// The receipts screen. GET /data/receipts has returned everything this needs
// since the feature was built - lineCount, hasImage and a full reconciliation
// block - and nothing had ever called it, so the only way to see a receipt was
// to find an invoice and click View on a cost row. A receipt with no invoice,
// like a tank of fuel, was invisible from the UI entirely.
//
// The LINES come from the expenses App already holds, not from another fetch:
// an expense row IS a receipt line (there is no receipt_lines table), and it
// carries receipt_id and linked_job_id. So filtering by job costs nothing.

const money = n => '$' + (Number(n) || 0).toFixed(2);
const clean = s => String(s || '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
const shortDate = s => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return s || '-';
  return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Number(m[2]) - 1] + ' ' + Number(m[3]) + ', ' + m[1];
};
const daysApart = (a, b) => {
  const x = Date.parse(String(a || '').slice(0, 10) + 'T00:00:00');
  const y = Date.parse(String(b || '').slice(0, 10) + 'T00:00:00');
  if (isNaN(x) || isNaN(y)) return null;
  return Math.round((y - x) / 86400000);
};

export default function Receipts({ expenses = [], jobs = [], invoices = [], clientName = () => '', fmt }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [jobFilter, setJobFilter] = useState('all');
  const [period, setPeriod] = useState('all');
  const [search, setSearch] = useState('');
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [open, setOpen] = useState(null);           // receipt id whose lines are expanded
  const [view, setView] = useState(null);           // the photo lightbox

  useEffect(() => {
    let alive = true;
    apiGet('/data/receipts')
      .then(r => { if (alive) setRows(toArr(r)); })
      .catch(e => { if (alive) setErr((e && e.message) || 'Could not load receipts.'); });
    return () => { alive = false; };
  }, []);

  // An expense row IS a receipt line. Group once rather than per render.
  const linesByReceipt = useMemo(() => {
    const m = {};
    for (const e of toArr(expenses)) {
      const rid = e.receipt_id ?? e.receiptId;
      if (rid == null || rid === '') continue;
      (m[rid] = m[rid] || []).push(e);
    }
    return m;
  }, [expenses]);

  const jobTitle = id => (toArr(jobs).find(j => j.id === Number(id)) || {}).title || ('job ' + id);
  const invNumber = id => (toArr(invoices).find(v => v.id === Number(id)) || {}).number || ('invoice ' + id);

  // Only flags that are unambiguous. A marker that fires on correct data is how
  // flags get ignored, and these are the ones a person cannot see by eye.
  const flagsFor = r => {
    const out = [];
    if (!r.hasImage) out.push({ k: 'photo', t: 'no photo', why: 'The amounts are recorded but nothing substantiates them. Re-scan if you still have the paper.' });
    if (r.reconciliation && r.reconciliation.ok === false) {
      out.push({ k: 'sum', t: 'does not add up', why: 'The line items do not sum to the printed total - a missed line, a wrong quantity or a slipped decimal.' });
    }
    const d = daysApart(r.purchase_date, (r.created_at || '').slice(0, 10));
    if (d != null && d > 45) out.push({ k: 'date', t: 'check the date', why: 'Dated ' + d + ' days before it was scanned. Fine if you were catching up, wrong if the date was misread.' });
    if (d != null && d < 0) out.push({ k: 'date', t: 'dated in the future', why: 'The purchase date is after the day it was scanned.' });
    const ls = linesByReceipt[r.id] || [];
    const stranded = ls.filter(e => Number(e.billable) === 1 && !e.linked_job_id && !e.linked_invoice);
    if (stranded.length) out.push({ k: 'loose', t: stranded.length + ' billable, unattached', why: 'Marked to rebill but pointing at no job or invoice, so it will never reach a customer.' });
    return out;
  };

  const years = useMemo(() => {
    const s = new Set();
    for (const r of toArr(rows)) { const y = String(r.purchase_date || '').slice(0, 4); if (/^\d{4}$/.test(y)) s.add(y); }
    return [...s].sort().reverse();
  }, [rows]);

  const jobOptions = useMemo(() => {
    const ids = new Set();
    for (const list of Object.values(linesByReceipt)) for (const e of list) if (e.linked_job_id) ids.add(Number(e.linked_job_id));
    return [...ids].sort((a, b) => a - b);
  }, [linesByReceipt]);

  const shown = useMemo(() => {
    let out = toArr(rows);
    if (jobFilter === 'none') {
      out = out.filter(r => !(linesByReceipt[r.id] || []).some(e => e.linked_job_id));
    } else if (jobFilter !== 'all') {
      out = out.filter(r => (linesByReceipt[r.id] || []).some(e => Number(e.linked_job_id) === Number(jobFilter)));
    }
    if (period !== 'all') out = out.filter(r => String(r.purchase_date || '').slice(0, 4) === period);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter(r =>
        String(r.vendor || '').toLowerCase().includes(q) ||
        String(r.total || '').includes(q) ||
        (linesByReceipt[r.id] || []).some(e => clean(e.description).toLowerCase().includes(q)));
    }
    if (onlyFlagged) out = out.filter(r => flagsFor(r).length > 0);
    return out;
  }, [rows, jobFilter, period, search, onlyFlagged, linesByReceipt]);

  const totals = useMemo(() => {
    let t = 0, tax = 0, withPhoto = 0;
    for (const r of shown) { t += Number(r.total) || 0; tax += Number(r.tax_amount) || 0; if (r.hasImage) withPhoto++; }
    return { t, tax, withPhoto };
  }, [shown]);

  // Fetched with the bearer token and wrapped in a blob:, because an <img>
  // cannot carry an Authorization header. Nothing bearer-capable reaches the
  // page, so a copied link is useless to anyone not signed in.
  const closeView = () => setView(v => { if (v && v.url) { try { URL.revokeObjectURL(v.url); } catch (e) {} } return null; });
  const viewPhoto = async (r) => {
    setView({ rid: r.id, label: r.vendor, loading: true });
    try {
      const res = await fetch(API_URL.replace('/api', '') + '/api/data/receipts/' + r.id + '/file', { headers: { Authorization: 'Bearer ' + getToken() } });
      if (!res.ok) { setView({ rid: r.id, label: r.vendor, error: await apiErrorMessage(res) }); return; }
      setView({ rid: r.id, label: r.vendor, url: URL.createObjectURL(await res.blob()) });
    } catch (e) {
      setView({ rid: r.id, label: r.vendor, error: (e && e.message) || 'Could not load the photo.' });
    }
  };

  // The index an accountant actually needs: one row per LINE, not per receipt,
  // with the Schedule C category already resolved.
  const exportCsv = () => {
    const q = s => '"' + String(s == null ? '' : s).replace(/"/g, '""') + '"';
    const head = ['Date','Vendor','Item','Qty','Unit','Unit cost','Amount','Category','Schedule C','Job','Invoice','Billable','Receipt #','Photo on file'];
    const out = [head.map(q).join(',')];
    for (const r of shown) {
      const ls = linesByReceipt[r.id] || [];
      if (!ls.length) {
        out.push([r.purchase_date, r.vendor, '(no line detail)', '', '', '', r.total, '', '', '', '', '', r.id, r.hasImage ? 'yes' : 'NO'].map(q).join(','));
        continue;
      }
      for (const e of ls) {
        out.push([
          e.date || r.purchase_date, r.vendor, clean(e.description), e.qty, e.unit, e.unit_cost, e.amount,
          e.category, e.schedule_c, e.linked_job_id ? jobTitle(e.linked_job_id) : '',
          e.linked_invoice ? invNumber(e.linked_invoice) : '', Number(e.billable) === 1 ? 'yes' : 'no',
          r.id, r.hasImage ? 'yes' : 'NO',
        ].map(q).join(','));
      }
    }
    const name = 'ampbooks-receipts-' + (period === 'all' ? 'all' : period) + '-' + new Date().toISOString().slice(0, 10) + '.csv';
    const url = URL.createObjectURL(new Blob([out.join('\n')], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const flagColor = { photo: D.danger, sum: D.danger, date: D.warning, loose: D.warning };

  return (
    <div>
      <div style={st.h1}>Receipts</div>
      <div style={{ color: D.muted, fontSize: 13, marginBottom: 16, maxWidth: 720, lineHeight: 1.5 }}>
        Every receipt you have scanned, with its photo and what each line was charged to.
        Anything that needs a look is marked.
      </div>

      {err && <div style={{ color: D.danger, fontSize: 13, marginBottom: 12 }}>{err}</div>}
      {rows === null && !err && <div style={{ color: D.muted, fontSize: 13 }}>Loading...</div>}

      {rows !== null && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
            <select style={{ ...st.input, maxWidth: 260 }} value={jobFilter} onChange={e => setJobFilter(e.target.value)}>
              <option value="all">All receipts</option>
              <option value="none">No job (overhead, invoices, personal)</option>
              {jobOptions.map(id => <option key={id} value={id}>{jobTitle(id)}</option>)}
            </select>
            <select style={{ ...st.input, maxWidth: 150 }} value={period} onChange={e => setPeriod(e.target.value)}>
              <option value="all">All time</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <input style={{ ...st.input, maxWidth: 220 }} placeholder="vendor, item or amount"
              value={search} onChange={e => setSearch(e.target.value)} />
            <label style={{ fontSize: 13, color: D.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={onlyFlagged} onChange={e => setOnlyFlagged(e.target.checked)} />
              only ones needing a look
            </label>
            <button style={{ ...st.btn(), marginLeft: 0 }} disabled={!shown.length} onClick={exportCsv}>
              Export CSV ({shown.length})
            </button>
          </div>

          <div style={{ background: D.surface, borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <span><span style={{ color: D.muted }}>showing</span> <b>{shown.length}</b> of {toArr(rows).length}</span>
            <span><span style={{ color: D.muted }}>total</span> <b>{money(totals.t)}</b></span>
            <span><span style={{ color: D.muted }}>sales tax</span> <b>{money(totals.tax)}</b></span>
            <span style={{ color: totals.withPhoto === shown.length ? D.success : D.warning }}>
              {totals.withPhoto} of {shown.length} with a photo
            </span>
          </div>

          {!shown.length && <div style={{ color: D.muted, fontSize: 13 }}>Nothing matches those filters.</div>}

          {shown.map(r => {
            const flags = flagsFor(r);
            const ls = linesByReceipt[r.id] || [];
            const isOpen = open === r.id;
            return (
              <div key={r.id} style={{ border: '1px solid ' + (flags.length ? D.warning : D.border), borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 12px', flexWrap: 'wrap', cursor: 'pointer' }}
                  onClick={() => setOpen(isOpen ? null : r.id)}>
                  <div style={{ minWidth: 92, color: D.muted, fontSize: 13 }}>{shortDate(r.purchase_date)}</div>
                  <div style={{ minWidth: 150, fontWeight: 600 }}>{r.vendor || '(no supplier)'}</div>
                  <div style={{ minWidth: 90, textAlign: 'right' }}>{money(r.total)}</div>
                  <div style={{ minWidth: 86, color: D.muted, fontSize: 12 }}>
                    {Number(r.tax_amount) > 0 ? 'tax ' + money(r.tax_amount) : 'no tax'}
                  </div>
                  <div style={{ minWidth: 60, color: D.muted, fontSize: 12 }}>{r.lineCount} line{r.lineCount === 1 ? '' : 's'}</div>
                  <div style={{ flex: 1 }} />
                  {flags.map(f => (
                    <span key={f.k} title={f.why}
                      style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1px solid ' + flagColor[f.k], color: flagColor[f.k] }}>
                      {f.t}
                    </span>
                  ))}
                  {r.hasImage
                    ? <button style={{ ...st.btn(), marginLeft: 0, fontSize: 12, padding: '3px 10px' }}
                        onClick={ev => { ev.stopPropagation(); viewPhoto(r); }}>Photo</button>
                    : <span style={{ fontSize: 11, color: D.muted }}>no photo</span>}
                  <span style={{ color: D.muted, fontSize: 12, width: 14, textAlign: 'center' }}>{isOpen ? '−' : '+'}</span>
                </div>

                {isOpen && (
                  <div style={{ borderTop: '1px solid ' + D.border, padding: '10px 12px' }}>
                    {flags.map(f => (
                      <div key={f.k} style={{ fontSize: 12, color: flagColor[f.k], marginBottom: 6 }}>{f.t} - {f.why}</div>
                    ))}
                    {!ls.length && <div style={{ color: D.muted, fontSize: 13 }}>No line detail stored for this receipt.</div>}
                    {!!ls.length && (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={st.table}>
                          <thead><tr>{['Item', 'Qty', 'Unit cost', 'Amount', 'Category', 'Charged to', 'Schedule C'].map(h => <th key={h} style={st.th}>{h}</th>)}</tr></thead>
                          <tbody>
                            {ls.map(e => (
                              <tr key={e.id}>
                                <td style={st.td}>{clean(e.description)}</td>
                                <td style={st.td}>{e.qty} {e.unit}</td>
                                <td style={st.td}>{Number(e.unit_cost) < 1 ? '$' + Number(e.unit_cost).toFixed(4) : money(e.unit_cost)}</td>
                                <td style={st.td}>{money(e.amount)}</td>
                                <td style={st.td}>{e.category || '-'}</td>
                                <td style={st.td}>
                                  {e.linked_job_id ? jobTitle(e.linked_job_id)
                                    : e.linked_invoice ? invNumber(e.linked_invoice)
                                    : e.category === 'Personal' ? <span style={{ color: D.muted }}>personal - not deductible</span>
                                    : <span style={{ color: D.muted }}>overhead</span>}
                                </td>
                                <td style={{ ...st.td, fontSize: 11, color: D.muted }}>{e.schedule_c || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: D.muted, marginTop: 8 }}>
                      printed subtotal {money(r.subtotal)} + tax {money(r.tax_amount)} = {money(r.total)}
                      {r.reconciliation && <> &middot; lines total {money(r.reconciliation.linesTotal)}</>}
                      {r.tax_source === 'estimated' && <> &middot; tax was estimated, not read off the paper</>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {view && (
        <div onClick={closeView}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={ev => ev.stopPropagation()}
            style={{ background: D.surface, borderRadius: 10, padding: 14, maxWidth: 'min(900px,95vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 600 }}>Receipt #{view.rid}{view.label ? ' - ' + view.label : ''}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {/* noreferrer matters: a URL in a Referer header hands it on. */}
                {view.url && <a href={view.url} target="_blank" rel="noreferrer" style={{ ...st.btn(), marginLeft: 0, textDecoration: 'none' }}>Open full size</a>}
                <button style={{ ...st.btn(), marginLeft: 0 }} onClick={() => viewPhoto({ id: view.rid, vendor: view.label })}>Reload</button>
                <button style={{ ...st.btn('danger'), marginLeft: 0 }} onClick={closeView}>Close</button>
              </div>
            </div>
            {view.loading && <div style={{ color: D.muted, fontSize: 13, padding: '40px 10px', textAlign: 'center' }}>Loading the photo...</div>}
            {view.error && <div style={{ color: D.danger, fontSize: 13, padding: '24px 10px' }}>{view.error}</div>}
            {view.url && <img src={view.url} alt={'Receipt ' + view.rid}
              onError={() => setView(v => v && ({ ...v, url: null, error: 'Could not load the image from storage. Press Reload to try again.' }))}
              style={{ maxWidth: '100%', maxHeight: '76vh', objectFit: 'contain', borderRadius: 6, background: '#000' }} />}
            <div style={{ fontSize: 11, color: D.muted }}>Served through AmpBooks, not a public link.</div>
          </div>
        </div>
      )}
    </div>
  );
}
